
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { ApprovalCard } from "./components/ApprovalCard";
import { toast } from "@/lib/toast";
import { useIsMobile } from "@/hooks/use-mobile";
import { supabase } from "@/integrations/supabase/client";
import { ApprovalPost } from "@/types/approval";

export default function ApprovalPage() {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<ApprovalPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const isMobile = useIsMobile();
  
  useEffect(() => {
    // Load real post data from Supabase by slug
    setLoading(true);
    
    const fetchData = async () => {
      try {
        if (!slug) {
          setLoading(false);
          return;
        }
        
        // Fetch post from database using slug
        const { data, error } = await supabase
          .from('approval_posts')
          .select('*')
          .eq('slug', slug)
          .maybeSingle();
          
        if (error) {
          console.error("Error fetching approval post:", error);
          toast.error("Erro ao carregar dados");
          setLoading(false);
          return;
        }
        
        if (!data) {
          setLoading(false);
          return;
        }
        
        // Format data to match ApprovalPost type
        const approvalPost: ApprovalPost = {
          id: data.id,
          title: data.title,
          client_name: data.client_name,
          image_url: data.image_url,
          status: data.status as 'pending' | 'approved' | 'rejected' | 'archived',
          feedback: data.feedback,
          platform: data.platform,
          shareable_link: data.shareable_link,
          slug: data.slug,
          created_at: data.created_at,
          updated_at: data.updated_at,
          caption: data.caption,
          post_date: data.post_date,
          instagram_handle: data.instagram_handle,
          post_type: data.post_type as 'static' | 'carousel' | 'reels',
          static_image: data.static_image || data.image_url,
          carousel_images: data.carousel_images || null,
          reels_video: data.reels_video || null
        };
        
        setPost(approvalPost);
      } catch (error) {
        console.error("Error in fetchData:", error);
        toast.error("Erro ao carregar dados");
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [slug]);
  
  const handleApprove = (id: string, feedback?: string) => {
    console.log("Aprovado:", id, feedback);
    setSuccess(true);
    
    // Send to API
    supabase
      .from('approval_posts')
      .update({ 
        status: 'approved', 
        feedback,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .then(({ error }) => {
        if (error) {
          console.error("Error approving post:", error);
          toast.error("Erro ao aprovar post");
          return;
        }
        toast.success("Aprovação registrada com sucesso!");
      });
  };
  
  const handleReject = (id: string, feedback: string) => {
    console.log("Rejeitado:", id, feedback);
    setSuccess(true);
    
    // Send to API
    supabase
      .from('approval_posts')
      .update({ 
        status: 'rejected', 
        feedback,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .then(({ error }) => {
        if (error) {
          console.error("Error rejecting post:", error);
          toast.error("Erro ao rejeitar post");
          return;
        }
        toast.success("Feedback enviado com sucesso!");
      });
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 sm:p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="h-8 w-8 mx-auto border-4 border-primary border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-muted-foreground">Carregando conteúdo...</p>
        </div>
      </div>
    );
  }
  
  if (!post) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 sm:p-6 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-xl sm:text-2xl font-bold mb-2">Post não encontrado</h1>
          <p className="text-muted-foreground">O link que você acessou não está mais disponível ou é inválido.</p>
        </div>
      </div>
    );
  }
  
  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 sm:p-6 flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
            </svg>
          </div>
          <h1 className="text-xl sm:text-2xl font-bold mb-2">Obrigado pelo seu feedback!</h1>
          <p className="text-muted-foreground mb-4">
            Sua resposta foi registrada com sucesso e nossa equipe já foi notificada.
          </p>
          <p className="text-sm text-gray-500">
            Esta janela pode ser fechada agora.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 sm:p-6">
      <div className="max-w-3xl mx-auto py-4 sm:py-8">
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold mb-2">Aprovação de Material</h1>
          <p className="text-muted-foreground px-4">
            Por favor, revise o material abaixo e nos forneça sua aprovação ou feedback.
          </p>
        </div>
        
        <div className="flex justify-center px-2 sm:px-0">
          <ApprovalCard
            id={post.id}
            title={post.title}
            image={post.image_url || ''}
            caption={post.caption}
            clientName={post.client_name}
            post_type={post.post_type}
            carousel_images={post.carousel_images}
            reels_video={post.reels_video}
            onApprove={handleApprove}
            onReject={handleReject}
          />
        </div>
      </div>
      
      <footer className="text-center mt-8 sm:mt-12 text-xs sm:text-sm text-muted-foreground px-4">
        <p>© 2023 Agência Digital | Todos os direitos reservados</p>
      </footer>
    </div>
  );
}
